import {
  faCirclePlus,
  faImage,
  faMessageDots,
  faPaperPlaneAlt,
  faWarning,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu } from '@mui/material';
import debounce from 'lodash/debounce';
import flatten from 'lodash/flatten';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import {
  UserAutocompleteDetailsFragment,
  useCoachMemberListLazyQuery,
  useSelfQuery,
  useSendBulkMessageMutation,
} from '../../../graphql/types';

import useTracking from '../../../hooks/useTracking';
import { BLACK2, WHITE } from '../../../theme';
import {
  EventCategories,
  EventLabels,
  EventNames,
  EventProfileCardNames,
  EventSubCategories,
} from '../../../utils/trackingEvents';
import Button, { ButtonSize, ButtonVariant } from '../../reusable/Button';
import { IconButton } from '../../reusable/Button/IconButton';
import { Divider } from '../../reusable/Divider';
import Flyout, { FlyoutItem } from '../../reusable/Flyout';
import { MemberBulkAutocomplete } from '../../reusable/MemberBulkAutocomplete';
import { SnackbarVariant } from '../../reusable/Snackbar';
import { useSnackbarContext } from '../../reusable/Snackbar/SnackbarContextProvider';
import Text, { TextKind } from '../../reusable/Text';
import TextAreaInput from '../../reusable/TextAreaInput';
import { ChatSize, useChatContext } from '../Context';
import { Header } from '../Header';
import { FilePreview } from '../Image';
import { useSendMessage } from '../useSendMessage';
import {
  StyledForm,
  StyledFormContent,
  StyledFormFooter,
  TemplateItem,
} from './styled';

interface CreateBulkChatValues {
  memberIds: Array<string>;
  bulkMessage: string;
}

interface CreateBulkChatProps {
  closeable?: boolean;
}

interface BulkTemplate {
  label: string;
  value: string;
}

const bulkTemplates: BulkTemplate[] = [
  { label: 'Member First Name', value: '{{first}}' },
];

export const CreateBulkChat: React.FC<CreateBulkChatProps> = ({
  closeable,
}) => {
  const methods = useForm<CreateBulkChatValues>({
    defaultValues: {
      memberIds: [],
      bulkMessage: '',
    },
  });
  const { handleSubmit, watch, formState } = methods;
  const { chatSize, closeChat, setChatListOpen } = useChatContext();
  const { setSnackbar } = useSnackbarContext();
  const { track } = useTracking();
  const { validateFileInput, uploadFile } = useSendMessage();

  const { data: selfData } = useSelfQuery();
  const [sendBulkChat] = useSendBulkMessageMutation();

  const selfId = selfData?.self?.id;
  const [
    fetchCoachMemberList,
    { data: userListData, loading: userListLoading },
  ] = useCoachMemberListLazyQuery();
  const membersFilter = useMemo(
    () => ({
      hasSignedIn: true,
    }),
    [],
  );

  const [selectedMembers, setSelectedMembers] = useState<
    UserAutocompleteDetailsFragment[]
  >([]);
  const [mergedMemberList, setMergedMemberList] = useState<
    UserAutocompleteDetailsFragment[]
  >([]);
  const [showAllPills, setShowAllPills] = useState(false);
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  const [showTemplateItems, setShowTemplateItems] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileUploadInputRef = useRef<HTMLInputElement>(null);

  const memberIdsWatcher = watch('memberIds');
  const messageWatcher = watch('bulkMessage');
  const actionMenuButtonRef = useRef(null);

  const reset = useCallback(() => {
    setShowAllPills(false);
    methods.setValue('memberIds', []);
    methods.setValue('bulkMessage', '');
    setSelectedMembers([]);
    setFile(null);
    setAutocompleteKey((prevKey) => prevKey + 1);
  }, [methods]);

  const handleFormSubmit = useCallback(
    async (values: CreateBulkChatValues) => {
      if (!selfId) {
        return;
      }

      const { memberIds, bulkMessage } = values;
      let messageContent = bulkMessage;

      if (file) {
        try {
          setUploadingFile(true);
          const { url } = await uploadFile(file);

          if (url) {
            messageContent = url;
          }
        } catch {
          setSnackbar({
            icon: faWarning,
            message: 'An error occurred while sending the bulk attachment.',
            variant: SnackbarVariant.Danger,
          });
        } finally {
          setUploadingFile(false);
        }
      }

      const bulkChat = await sendBulkChat({
        variables: {
          input: {
            destinations: memberIds.map((id) => {
              return { userId: id };
            }),
            text: messageContent,
          },
        },
        refetchQueries: ['chats'],
        onError: () =>
          setSnackbar({
            icon: faWarning,
            message: 'An error occurred while sending the bulk message.',
            variant: SnackbarVariant.Danger,
            testId: 'bulk-chat-error',
          }),
      });

      if (!bulkChat.errors) {
        setSnackbar({
          icon: faPaperPlaneAlt,
          message: 'Message sent successfully! This can take a few minutes.',
          variant: SnackbarVariant.Success,
          testId: 'bulk-chat-success',
        });
        closeChat();
        reset();
        setChatListOpen(true);
      }
    },
    [
      selfId,
      sendBulkChat,
      setSnackbar,
      closeChat,
      reset,
      setChatListOpen,
      file,
      uploadFile,
    ],
  );

  const handleAutocompleteInput = debounce((value: string) => {
    fetchCoachMemberList({
      variables: {
        membersFilter: {
          ...(value ? { name: value } : {}),
          ...membersFilter,
        },
        input: {
          first: 10,
        },
      },
    });
  }, 500);

  const handleSelection = (
    selectedUsers: UserAutocompleteDetailsFragment[],
  ) => {
    const mergedSelectedUsers = [
      ...memberIdsWatcher,
      ...selectedUsers.map((user) => user.id),
    ];
    methods.setValue('memberIds', mergedSelectedUsers);
    setAutocompleteKey((prevKey) => prevKey + 1);
  };

  const handleDeletePill = (deletedUserId: string) => {
    methods.setValue(
      'memberIds',
      memberIdsWatcher.filter((selectedUser) => selectedUser !== deletedUserId),
    );
    setAutocompleteKey((prevKey) => prevKey + 1);
  };

  const handleTemplateMenuOpen = () => {
    setShowTemplateItems(true);
    track(EventNames.MenuItemSelect, {
      event_category: EventCategories.Chat,
      event_subcategory: EventSubCategories.BulkTemplate,
      event_label: 'Browse bulk message templates',
    });
  };

  const handleTemplateClick = (template: string) => {
    setShowTemplateItems(false);
    const prevMessage = methods.getValues('bulkMessage') || '';
    const newMessage =
      prevMessage.trim() !== ''
        ? `${prevMessage.trim()} ${template}`
        : template;
    methods.setValue('bulkMessage', newMessage);
  };

  const trackActionMenuOpen = () => {
    track(EventNames.PlusIconClick, {
      event_category: EventCategories.Chat,
    });
  };

  const handleFlyoutOpen = () => {
    setShowTemplateItems(false);
    trackActionMenuOpen();
  };

  const handleSelectFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    track(EventNames.FileSelection, {
      chat_id: 'bulk-chat',
      event_category: EventCategories.Chat,
      event_subcategory: EventSubCategories.Image,
      event_label: event.target.files?.[0].type.split('/')[1],
    });
    setFile(validateFileInput(event));
  };

  const handleRemoveFile = () => {
    setFile(null);
    track(EventNames.RemoveAttachment, {
      event_category: EventCategories.Chat,
      event_subcategory: EventSubCategories.Image,
      chat_id: 'bulk-chat',
    });
  };

  const handleUploadImageClick = () => {
    track(EventNames.MenuItemSelect, {
      event_category: EventCategories.Chat,
      event_label: 'Upload an image',
      event_subcategory: EventSubCategories.Image,
      chat_id: 'bulk-chat',
    });
    if (fileUploadInputRef.current?.value) {
      fileUploadInputRef.current.value = '';
    }
    fileUploadInputRef.current?.click();
  };

  const getPreviewMaxHeight = useCallback(() => {
    if (chatSize === ChatSize.Maximized) {
      return showAllPills ? '340px' : '500px';
    }
    return showAllPills ? '60px' : '230px';
  }, [chatSize, showAllPills]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: biome 2.X migration
  useEffect(() => {
    // on mount, fetch the member list
    if (chatSize !== ChatSize.Closed) {
      fetchCoachMemberList({
        variables: {
          input: {
            first: 10,
          },
          membersFilter,
        },
      });
    }
    if (chatSize === ChatSize.Closed) {
      methods.reset();
    }
  }, [fetchCoachMemberList, chatSize, methods.reset]);

  useEffect(() => {
    const allClients = userListData?.self?.coachData?.clients.edges.map(
      ({ node }) => node,
    );

    const allFetchedMembers = allClients?.map((client) =>
      client.users.edges.map(({ node }) => node),
    );

    setMergedMemberList([
      ...flatten(allFetchedMembers).filter(
        ({ id }) =>
          !selectedMembers.some(
            ({ id: selectedMemberId }) => id === selectedMemberId,
          ),
      ),
      ...selectedMembers,
    ]);
  }, [userListData?.self?.coachData?.clients.edges, selectedMembers]);

  useEffect(() => {
    const newSelectedMembers = memberIdsWatcher
      .filter((id) => !selectedMembers.some((member) => member.id === id))
      .map((id) => mergedMemberList.find((member) => member.id === id))
      .filter((member) => member !== undefined);

    if (newSelectedMembers.length > 0) {
      setSelectedMembers((currentList) => [
        ...currentList,
        ...newSelectedMembers,
      ]);
    }
  }, [memberIdsWatcher, mergedMemberList, selectedMembers]);

  useEffect(() => {
    setMergedMemberList((currentList) => [
      ...currentList.filter(
        ({ id }) =>
          !selectedMembers.some(({ id: existingId }) => existingId === id),
      ),
      ...selectedMembers,
    ]);
  }, [selectedMembers]);

  return (
    <>
      <Header
        content={{ title: 'New message', hideEmptyElements: true }}
        closeable={closeable}
        resetSelectedMembers={reset}
      />
      <FormProvider {...methods}>
        <StyledForm
          onSubmit={handleSubmit(handleFormSubmit)}
          data-testid="createBulkChat-form"
        >
          <StyledFormContent>
            <Controller
              name="memberIds"
              rules={{
                required: 'At least one member is required',
              }}
              render={({ field }) => (
                <MemberBulkAutocomplete
                  key={autocompleteKey}
                  name={field.name}
                  selectedMemberIds={field.value || []}
                  members={mergedMemberList || []}
                  onInputChange={handleAutocompleteInput}
                  onSelection={handleSelection}
                  onDeletePill={handleDeletePill}
                  placeholder="Search for members"
                  loading={userListLoading}
                  trackingData={{
                    event_category: EventCategories.Chat,
                    card_name: EventProfileCardNames.CreateBulkChat,
                  }}
                  showAllPills={showAllPills}
                  setShowAllPills={setShowAllPills}
                />
              )}
            />
            {file ? (
              <FilePreview
                file={file}
                loading={uploadingFile}
                onRemoveFile={handleRemoveFile}
                previewStyle={{ maxHeight: getPreviewMaxHeight() }}
              />
            ) : (
              <TextAreaInput
                formRegister={methods.register('bulkMessage', {
                  required: 'Message is required',
                })}
                placeholder="Write a message"
                backgroundColor={WHITE}
                height="100%"
                trackingData={{
                  event_category: EventCategories.Chat,
                  card_name: EventProfileCardNames.CreateBulkChat,
                  event_label: EventLabels.CreateBulkChat,
                }}
                onFocus={() => setShowAllPills(false)}
              />
            )}
          </StyledFormContent>

          <Divider $marginBottom={0} $marginTop={0} />

          <StyledFormFooter>
            <Button
              type="submit"
              variant={ButtonVariant.Dark}
              icon={<FontAwesomeIcon icon={faPaperPlaneAlt} />}
              trackingData={{
                event_category: EventCategories.Chat,
                card_name: EventProfileCardNames.CreateBulkChat,
                event_label: EventLabels.Create,
              }}
              disabled={formState.isSubmitting || uploadingFile}
              loading={formState.isSubmitting || uploadingFile}
              ariaLabel="Send message"
            >
              Send
            </Button>

            <input
              data-testid="bulk-chat-image-upload"
              ref={fileUploadInputRef}
              type="file"
              accept={[
                'image/jpeg',
                'image/jpg',
                'image/png',
                'application/pdf',
              ].join(',')}
              multiple={false}
              style={{
                display: 'none',
              }}
              onChange={handleSelectFile}
            />

            <Flyout
              flyoutId="ActionMenu"
              menuProps={{
                anchorOrigin: { vertical: 'top', horizontal: 'left' },
                transformOrigin: { vertical: 'bottom', horizontal: 'left' },
              }}
              onOpen={handleFlyoutOpen}
              trigger={
                <IconButton
                  ariaLabel="Open more actions"
                  size={ButtonSize.Small}
                  icon={
                    <FontAwesomeIcon
                      ref={actionMenuButtonRef}
                      fontSize={20}
                      icon={faCirclePlus}
                    />
                  }
                />
              }
              tooltipContent="Open more actions"
            >
              <FlyoutItem
                onClick={handleTemplateMenuOpen}
                textKind={TextKind.LabelL}
                startIcon={
                  <FontAwesomeIcon icon={faMessageDots} color={BLACK2} />
                }
              >
                Browse template tags
              </FlyoutItem>
              <FlyoutItem
                onClick={() => handleUploadImageClick()}
                textKind={TextKind.LabelL}
                disabled={Boolean(messageWatcher)}
                startIcon={<FontAwesomeIcon icon={faImage} color={BLACK2} />}
              >
                Upload a file
              </FlyoutItem>
            </Flyout>
            <Menu
              style={{ marginTop: -16 }}
              disableScrollLock
              anchorEl={actionMenuButtonRef.current}
              open={showTemplateItems}
              onBlur={() => setShowTemplateItems(false)}
              MenuListProps={{
                disablePadding: true, // Disable default MUI Menu padding of 8px top and bottom
              }}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 8 }}
            >
              {bulkTemplates.map((template) => (
                <TemplateItem
                  data-testid={`template-${template.value}`}
                  key={template.value}
                  onClick={() => handleTemplateClick(template.value)}
                >
                  <Text kind={TextKind.BodySM} textTransform="none">
                    {template.label}
                  </Text>
                </TemplateItem>
              ))}
            </Menu>
          </StyledFormFooter>
        </StyledForm>
      </FormProvider>
    </>
  );
};
